import React, { useCallback, useMemo, useState } from "react";
import styled from "styled-components/native";
import { Platform, Pressable } from "react-native";
import { SearchListAdContainerProps } from "./types";
import { useTheme } from "styled-components/native";
import { EventType } from "@utils/analytics";
import { useUserAnalytics } from "../../../hooks/useAnalytics";
import { BannerActionEnum } from "../AdView/BannerAd/types";
import { createAdProps } from "../AdView/BannerAd/utils";
import Animated, {
  useAnimatedStyle,
  withSpring,
  useSharedValue,
  useAnimatedReaction,
} from "react-native-reanimated";
import { Image as ExpoImage } from "expo-image";
import { useSelector } from "react-redux";
import { SpaceType } from "@components/atoms/Spacer/types";
import { useGlobalData } from "@redux/slices/globalSlice";
import { useIsFoldable } from "@navigations/utils/FoldableWrapper";
import { parseAndNavigate } from "@utils/product/searchResultsNavigation";
import {
  getImageSource,
  getIOSImageProps,
  ImageSizePreset,
} from "@utils/image/imageOptimizer";

const SearchResultListAdItem = ({
  item,
  xOffset,
  isFilterVisible: filterVisible,
  enableMobileWebView,
}: SearchListAdContainerProps) => {
  const theme = useTheme();
  const ImageComponent = ExpoImage;
  const adWidth = useSelector((state: any) => state.appState.adWidth);
  const { deviceWidth: width } = useIsFoldable();

  const sizes = {
    height: filterVisible === "false" ? 56 : 48,
  };

  if (theme.isLargeScreen) {
    if (filterVisible === "false" || !filterVisible) {
      if (width >= 1200 && width < 1280) {
        sizes.height = 54;
      } else if (width >= 1280 && width < 1366) {
        sizes.height = 58;
      } else if (width >= 1366 && width < 1440) {
        sizes.height = 62;
      } else if (width >= 1440 && width < 1512) {
        sizes.height = 65;
      } else if (width >= 1512 && width < 1680) {
        sizes.height = 68;
      } else if (width >= 1680 && width < 1728) {
        sizes.height = 76;
      } else if (width >= 1728 && width < 1920) {
        sizes.height = 78;
      } else if (width >= 1920 && width < 2254) {
        sizes.height = 86;
      } else {
        sizes.height = 100;
      }
    } else {
      if (width >= 1200 && width < 1280) {
        sizes.height = 35;
      } else if (width >= 1280 && width < 1366) {
        sizes.height = 38;
      } else if (width >= 1366 && width < 1440) {
        sizes.height = 42;
      } else if (width >= 1440 && width < 1512) {
        sizes.height = 46;
      } else if (width >= 1512 && width < 1680) {
        sizes.height = 48;
      } else if (width >= 1680 && width < 1728) {
        sizes.height = 56;
      } else if (width >= 1728 && width < 1920) {
        sizes.height = 58;
      } else if (width >= 1920 && width < 2254) {
        sizes.height = 66;
      } else {
        sizes.height = 80;
      }
    }
  }

  const adHeight = useMemo(() => {
    return filterVisible === "false" || !filterVisible
      ? theme.isLargeScreen
        ? sizes.height
        : 130
      : theme.isLargeScreen
      ? sizes.height
      : 80;
  }, [filterVisible, sizes]);

  const [imageUri, setImageUri] = useState(
    item?.media_url_url ? item?.media_url_url : ""
  );
  const { trackAdEvent } = useUserAnalytics();
  const { products } = useGlobalData();
  const handlePress = useCallback(async () => {
    if (item?.media_redirect_link_v2) {
      trackAdEvent(
        EventType.VIEW_BANNER,
        createAdProps(BannerActionEnum.clickThrough, item, {
          campaign: item.campaign,
        })
      );
      if (item?.media_redirect_link_v2) {
        await parseAndNavigate(item?.media_redirect_link_v2, products);
      }
    }
  }, [item?.media_redirect_link_v2]);

  const smoothedOffset = useSharedValue(0);

  useAnimatedReaction(
    () => xOffset.value,
    (currentOffset) => {
      smoothedOffset.value = withSpring(currentOffset, {
        mass: 0.01,
        damping: 30,
        stiffness: 1000,
        restDisplacementThreshold: 0.01,
        restSpeedThreshold: 0.01,
        overshootClamping: true,
      });
    },
    [xOffset]
  );

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [
      {
        translateX:
          Platform.OS === "web" ? smoothedOffset.value : xOffset.value,
      },
    ],
  }));

  return (
    <Animated.View
      style={[
        {
          width: theme.isMobile && !enableMobileWebView ? "100%" : adWidth,
          height: theme.isMobile && !enableMobileWebView ? "auto" : adHeight,
          overflow: "hidden",
        },
        animatedStyle,
      ]}
    >
      <Pressable onPress={() => handlePress()}>
        <ParentContainer
          height={adHeight}
          width={adWidth}
          enableMobileWebView={enableMobileWebView}
        >
          <ImageContainer>
            <ImageComponent
              key={filterVisible}
              style={{
                height: theme.isMobile && !enableMobileWebView ? 155 : adHeight,
                width:
                  theme.isMobile && !enableMobileWebView
                    ? "100%"
                    : adWidth - SpaceType.Big * 2,
                borderRadius: theme.isMobile && !enableMobileWebView ? 8 : 0,
              }}
              contentFit={Platform.OS === "web" ? "fill" : "cover"}
              source={getImageSource(imageUri, ImageSizePreset.AD_BANNER)}
              {...getIOSImageProps()}
              onError={() => {
                setImageUri("");
              }}
            />
          </ImageContainer>
        </ParentContainer>
      </Pressable>
    </Animated.View>
  );
};

const ParentContainer = styled.View<{
  height?: number;
  width?: number;
  enableMobileWebView?: boolean;
}>`
  height: ${({ theme, height, enableMobileWebView }) =>
    theme.isMobile && !enableMobileWebView ? 155 : height}px;
  width: ${({ theme, width, enableMobileWebView }) =>
    theme.isMobile && !enableMobileWebView
      ? "100%"
      : `${width - SpaceType.Big * 2}px`};
  justify-content: center;
  align-items: center;
  border-radius: ${({ theme, enableMobileWebView }) =>
    theme.isMobile && !enableMobileWebView ? 8 : 0}px;
  background-color: #ffffff;
`;

const ImageContainer = styled.View<any>`
  width: 100%;
  justify-content: center;
  align-items: center;
`;

export default SearchResultListAdItem;
